import type { JSNode, Narrowable } from './MultiKey';
import { MultiKey } from './MultiKey';
import jsonPointer from 'json-pointer';
import { isArray } from 'lodash';
import { DistanceMap } from '../distances/distanceMap';
import type { Distances, RouteData, RouteParams } from '../distances/context';
import type { ScenarioLocation } from '../../lib/planningData';
import type { Event, Leg } from './TimeData';
import type { WaitEvent } from './TimeData';
import type { ClientId } from './ClientId';
import { generateClientId } from './ClientId';
import type { FnType } from '../../lib/util';
import { isDefined } from '../../lib/util';
import type { User } from '../../state/zustand';
import type { CargoRanges } from './Capacity';

type JSONReference = {
  $ref: string;
};

/**
 * There are so many places in this file where we just can't know the types.
 * Even if we could do some additional error checking on some rows the only benefit
 * would be throwing a more informative Error, at the cost of readability.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

function isJSONReference(x: any): x is JSONReference {
  return typeof x?.$ref === 'string';
}

export type FunctionReference = {
  __type: 'Function';
  name: string;
  fnId: ClientId;
};

export type MultiKeyJSON<KeyType extends Narrowable[], ValueType> = {
  __type: 'MultiKey';
  value: [KeyType[number], JSNode<KeyType, ValueType>][];
  keyLength: number;
};
export type JSONSpecialObject =
  | {
      __type: 'Map' | 'Set' | 'Date';
      value: unknown;
    }
  | MultiKeyJSON<Narrowable[], unknown>
  | {
      __type: 'DistanceMap';
      value: {
        map: MultiKey<RouteData, RouteParams>;
        locationsById: Map<number, ScenarioLocation>;
        draft: number | null | undefined;
        noGoZones: string;
      };
    }
  | FunctionReference;

function isSpecialObject(x: any): x is JSONSpecialObject {
  return (
    typeof x?.__type === 'string' &&
    (typeof x?.value !== `undefined` || typeof x?.fnId !== `undefined`)
  );
}

const KEY_NULL = '__null';
const KEY_UNDEFINED = '__undefined';

const keysToPointerPath = (
  keys: (string | number | symbol | null | undefined)[],
): string => {
  const path: string[] = [];

  for (const key of keys) {
    if (key === null) {
      path.push(KEY_NULL);
    } else if (key === undefined) {
      path.push(KEY_UNDEFINED);
    } else if (typeof key === 'symbol') {
      throw new Error(`Cannot serialize Symbol keys`);
    } else if (typeof key === 'number') {
      path.push(`__num_${key}`);
    } else {
      path.push(key);
    }
  }

  return jsonPointer.compile(path);
};

const pointerPathToKeys = (
  path: string,
): (string | number | null | undefined)[] => {
  const parts = jsonPointer.parse(path);
  const result: (string | number | null | undefined)[] = [];
  for (const part of parts) {
    if (part === KEY_NULL) {
      result.push(null);
    } else if (part === KEY_UNDEFINED) {
      result.push(undefined);
    } else if (part.startsWith('__num_')) {
      result.push(+part.slice(6));
    } else {
      result.push(part);
    }
  }
  return result;
};

const FINAL_NODE = Symbol('IS_FINAL');
type Final<T> = [typeof FINAL_NODE, T];
const isFinal = (x: any): x is Final<any> => x?.[0] === FINAL_NODE;
/** Recursively updates every node in the nested structure using the updater fn */
function updateNested(
  obj: unknown,
  path: string[],
  mutate: boolean,
  fn: (item: unknown, path: string[]) => unknown,
): unknown {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }
  if (isFinal(obj)) {
    return obj[1];
  }
  let result = obj;
  if (obj instanceof Map) {
    result = mutate ? obj : new Map(obj);
    for (const [key, item] of obj) {
      const currentPath = path.concat([key]);
      let updatedItem = fn(item, currentPath);
      updatedItem = updateNested(updatedItem, currentPath, mutate, fn);
      (result as typeof obj).set(key, updatedItem);
    }
  } else if (obj instanceof MultiKey) {
    result = mutate ? obj : obj.clone();
    obj.forEach((item, mkPath: string[]) => {
      const currentPath = path.concat(mkPath);
      let updatedItem = fn(item, currentPath);
      updatedItem = updateNested(updatedItem, currentPath, mutate, fn);
      (result as typeof obj).set(updatedItem, ...mkPath);
    });
  } else if (obj?.constructor === Object || obj instanceof Array) {
    result = mutate ? obj : obj instanceof Array ? [...obj] : { ...obj };
    for (const key of Object.keys(obj)) {
      const currentPath = path.concat(key);
      const item = (obj as any)[key];
      let updatedItem = fn(item, currentPath);
      updatedItem = updateNested(updatedItem, currentPath, mutate, fn);
      (result as any)[key] = updatedItem;
    }
  }
  return result;
}

// Type for allowed primitives and class instances
type SerializableType =
  | string
  | number
  | boolean
  | undefined
  | null
  | Event
  | Leg
  | WaitEvent
  | Set<any>
  | Map<any, SerializableValue>
  | ReadonlyMap<any, SerializableValue>
  | Date
  | MultiKey<SerializableValue, Narrowable[]>
  | Distances
  | DistanceMap
  | User
  | CargoRanges;

// Recursive type for nested structures
export type SerializableValue<T = never> =
  | SerializableType
  | T
  | SerializableValue<T>[]
  | ReadonlyArray<SerializableValue<T>>
  | { [key: string | number | symbol]: SerializableValue<T> };

/** JSON stringify, but handles several advanced types in a special way (Set, Map, Date, MultiKey)
 * Also uses JSON references https://json-spec.readthedocs.io/reference.html to avoid duplication of
 * referentially equal objects (which as a by product also solves circular structures).
 */
export function jsonStringify<FunctionsMap>(
  root:
    | SerializableValue<
        FunctionsMap extends Map<unknown, infer Fns> ? Fns : never
      >[]
    | {
        [key: string]: SerializableValue<
          FunctionsMap extends Map<unknown, infer Fns> ? Fns : never
        >;
      },
  opts: {
    /** If functions are included, this output map needs to be given where each function is mapped to a serialized ID.
     * If not provided, meeting any functions in data will throw an error. */
    functionsMap?: FunctionsMap & Map<unknown, FnType>;
  } = {},
): string {
  const paths = new WeakMap<object, string>();

  paths.set(root, '');

  /** Replace nodes in the nested structure with references, if they are duplicates */
  const newRoot = updateNested(root, [], false, (item, currentPath) => {
    if (typeof item === 'object' && item !== null) {
      const refPath = paths.get(item);
      if (refPath === undefined) {
        paths.set(item, keysToPointerPath(currentPath));
      } else {
        return {
          $ref: refPath,
        } as JSONReference;
      }
    }
    return item;
  });

  // Traverse the object and replace any repeating item with a reference object
  function serializeComplexObjects(obj: any): void {
    for (const key of Object.keys(obj)) {
      let item = obj[key];

      let wasExpanded = false;
      if (item instanceof MultiKey) {
        obj[key] = item.toJS();
        wasExpanded = true;
      } else if (item instanceof DistanceMap) {
        obj[key] = item.toJS();
        wasExpanded = true;
      } else if (item instanceof Map) {
        obj[key] = {
          __type: 'Map',
          value: [...item.entries()],
        } as JSONSpecialObject;
        wasExpanded = true;
      } else if (item instanceof Set) {
        obj[key] = {
          __type: 'Set',
          value: [...item.values()],
        } as JSONSpecialObject;
        wasExpanded = true;
      } else if (item instanceof Date) {
        obj[key] = {
          __type: 'Date',
          value: item.toISOString(),
        } as JSONSpecialObject;
        wasExpanded = true;
      } else if (opts.functionsMap && typeof item === 'function') {
        const fnId = generateClientId();
        opts.functionsMap.set(fnId, item);
        // noinspection UnnecessaryLocalVariableJS
        const serialized: JSONSpecialObject = {
          __type: 'Function',
          name: item.name,
          fnId,
        };
        obj[key] = serialized;
        wasExpanded = false;
      } else if (typeof item === 'function') {
        throw new Error(`Cannot JSON serialize functions`);
      } else if (item instanceof Symbol) {
        throw new Error(`Cannot JSON serialize Symbols`);
      }

      item = obj[key];

      if (typeof item !== 'object' || item === null) {
        continue;
      }

      // Make a shallow copy because we may mutate the entries
      if (isArray(item)) {
        obj[key] = item = [...item];
      } else {
        obj[key] = item = { ...item };
      }
      serializeComplexObjects(wasExpanded ? item.value : item);
    }
  }

  serializeComplexObjects(newRoot);

  return JSON.stringify(newRoot);
}

function getAtPath(obj: unknown, path: string): unknown {
  const props = pointerPathToKeys(path);

  if (props[0] === '') {
    return obj;
  }

  let current = obj;
  for (let i = 0; i < props.length; ++i) {
    const prop = props[i];
    if (current instanceof Map) {
      current = current.get(prop);
    } else if (current instanceof MultiKey) {
      const keyLength = current.keyLength;
      const key = props.slice(i, i + keyLength);
      i += keyLength - 1;
      current = current.get(...key);
    } else if (typeof current === 'object' && current !== null) {
      if (!isDefined(prop)) {
        console.error(`Invalid object key: ${prop}`);
        return undefined;
      }
      current = (current as Record<typeof prop, unknown>)[prop];
    }
  }
  return current;
}

/** JSON parse, but handles several advanced types in a special way (Set, Map, Date, MultiKey)
 * Also uses JSON references https://json-spec.readthedocs.io/reference.html to restore
 * referentially equal objects (which as a by product also restores circular structures).
 */
export function jsonParse<Result = Record<string, string>>(
  str: string,
  functionParser?: (fn: FunctionReference) => (...params: unknown[]) => unknown,
): Result {
  function reviveSpecialObjects(obj: any, path: string[]): void {
    if (typeof obj !== 'object' || obj === null) {
      return;
    }

    for (const key of Object.keys(obj)) {
      reviveSpecialObjects(obj[key], path.concat([key]));

      const child = obj[key];

      if (isSpecialObject(child)) {
        if (child?.__type === 'MultiKey') {
          obj[key] = MultiKey.fromJS(child);
        } else if (child?.__type === 'DistanceMap') {
          obj[key] = DistanceMap.fromJS(child.value);
        } else if (child?.__type === 'Map') {
          obj[key] = new Map(child.value as any);
        } else if (child?.__type === 'Set') {
          obj[key] = new Set(child.value as any);
        } else if (child?.__type === 'Date') {
          obj[key] = new Date(child.value as any);
        } else if (child?.__type === 'Function') {
          if (!functionParser) {
            throw new Error(
              `Cannot deserialize function without functionParser`,
            );
          }
          obj[key] = functionParser(child);
        } else {
          throw new Error(
            `Unrecognized special value type in deserializing ${child}`,
          );
        }
      }
    }
  }

  function reviveRefs(root: unknown): void {
    updateNested(root, [], true, (item) => {
      if (isJSONReference(item)) {
        return [FINAL_NODE, getAtPath(root, item.$ref)] as Final<any>;
      }
      return item;
    });
  }

  const result = JSON.parse(str);
  reviveSpecialObjects(result, []);
  reviveRefs(result);
  return result;
}
